/**
 * frontend/src/screens/ModelViewer.js
 *
 * Interactive WebGL 3D model viewer using Three.js.
 * Loads a single GLB file from OneDrive via admin proxy — GLB is the
 * single source of truth end-to-end (geometry + textures in one file).
 *
 * Features:
 *   - Auto-rotating turntable (can be paused by dragging)
 *   - Mouse/touch drag to orbit
 *   - Scroll to zoom
 *   - Neutral grey background
 *   - Loading spinner while model loads
 *   - Error state if files can't be loaded
 */

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// Build proxy URL for admin-authed file access
const proxyUrl = (path) =>
  `/api/admin/download?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(path.split('/').pop())}`;

export default function ModelViewer({ files, onClose }) {
  const mountRef  = useRef(null);
  const sceneRef  = useRef(null);
  const [status,  setStatus]  = useState('loading');
  const [message, setMessage] = useState('Loading 3D model…');

  // Find the GLB model file for the useEffect dependency
  const modelKey = files?.find(f => f.name.endsWith('.glb'))?.path || '';

  useEffect(() => {
    if (!files?.length) {
      setStatus('error');
      setMessage('No model files available');
      return;
    }

    const container = mountRef.current;
    if (!container) return;

    // ── Scene setup ───────────────────────────────────────────────────────────
    const width  = container.clientWidth;
    const height = container.clientHeight;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const scene  = new THREE.Scene();
    scene.background = new THREE.Color(0x2a2a2a);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000);
    camera.position.set(0, 1, 3);

    // No dynamic lighting — texture has baked lighting from Meshy
    // MeshBasicMaterial displays texture exactly as-is, matching Meshy's preview
    const ambient = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambient);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping  = true;
    controls.dampingFactor  = 0.08;
    controls.minDistance    = 0.5;
    controls.maxDistance    = 20;
    controls.autoRotate     = true;
    controls.autoRotateSpeed = 2.5;

    sceneRef.current = { renderer, scene, camera, controls };

    // ── Load model ────────────────────────────────────────────────────────────
    const loadModel = async () => {
      try {
        setMessage('Loading model file…');

        const glbFile = (files || []).find(f => f.name.endsWith('.glb'));
        if (!glbFile) {
          setStatus('error');
          setMessage('No GLB file found');
          return;
        }

        setMessage('Loading geometry…');
        const glbArrayBuffer = await fetch(proxyUrl(glbFile.path), { credentials: 'include' }).then(r => r.arrayBuffer());
        const glbLoader = new GLTFLoader();

        const gltf = await new Promise((resolve, reject) => {
          glbLoader.parse(glbArrayBuffer, '', resolve, reject);
        });
        const object = gltf.scene;

        // GLB textures already carry Meshy's baked lighting — flatten to
        // MeshBasicMaterial (same treatment as the old OBJ path) so the
        // viewer shows the texture as-is rather than relit as PBR.
        object.traverse(child => {
          if (child.isMesh && child.material?.map) {
            const map = child.material.map;
            map.colorSpace  = THREE.SRGBColorSpace;
            map.needsUpdate = true;
            child.material = new THREE.MeshBasicMaterial({ map });
            child.material.needsUpdate = true;
          }
        });

        // Center and scale model to fit view
        const box    = new THREE.Box3().setFromObject(object);
        const center = box.getCenter(new THREE.Vector3());
        const size   = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale  = 2 / maxDim;

        object.scale.setScalar(scale);

        // Recompute box after scaling to get correct center
        const scaledBox    = new THREE.Box3().setFromObject(object);
        const scaledCenter = scaledBox.getCenter(new THREE.Vector3());

        // Offset model so its center is at scene origin
        object.position.sub(scaledCenter);

        scene.add(object);

        // Position camera to look at origin
        const dist = maxDim * scale * 2;
        camera.position.set(0, 0, dist);
        controls.target.set(0, 0, 0);
        controls.update();

        setStatus('ready');

      } catch (err) {
        console.error('[ModelViewer] Load error:', err);
        setStatus('error');
        setMessage('Failed to load 3D model: ' + err.message);
      }
    };

    loadModel();

    // ── Render loop ───────────────────────────────────────────────────────────
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // ── Resize handler ────────────────────────────────────────────────────────
    const handleResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // ── Cleanup ───────────────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [modelKey]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      {/* Header */}
      <div style={{ position:'absolute', top:0, left:0, right:0, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', background:'rgba(0,0,0,0.6)' }}>
        <div style={{ color:'#fff', fontSize:14, fontWeight:600 }}>3D Model Preview</div>
        <div style={{ display:'flex', gap:16, alignItems:'center' }}>
          <span style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>Drag to rotate · Scroll to zoom</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#fff', fontSize:22, cursor:'pointer', lineHeight:1, padding:4 }}>✕</button>
        </div>
      </div>

      {/* Viewer */}
      <div ref={mountRef} style={{ width:'85vw', height:'80vh', borderRadius:8, overflow:'hidden', position:'relative' }}>
        {/* Loading / Error overlay */}
        {status !== 'ready' && (
          <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#2a2a2a', zIndex:10 }}>
            {status === 'loading' && (
              <>
                <div style={{ width:40, height:40, border:'3px solid rgba(255,255,255,0.2)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.8s linear infinite', marginBottom:16 }} />
                <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13 }}>{message}</div>
              </>
            )}
            {status === 'error' && (
              <>
                <div style={{ fontSize:32, marginBottom:12 }}>⚠️</div>
                <div style={{ color:'#f87171', fontSize:13, textAlign:'center', maxWidth:300 }}>{message}</div>
              </>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
